import { Component, computed, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  School,
  SchoolyearService,
  getNextRegistrationDeadline,
  resolveRegistrationDeadline,
} from '@awo-care/shared-core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-school-select',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
  ],
  template: `
    <mat-form-field
      appearance="outline"
      class="school-select"
      data-testid="school-select"
    >
      <mat-label i18n="@@lib.school-select.label">Schule</mat-label>
      <mat-select
        id="school-select"
        [formControl]="schoolControl"
        [compareWith]="compareSchools"
        panelClass="school-select-panel"
        i18n-placeholder="@@lib.school-select.placeholder"
        placeholder="Schule auswählen"
      >
        @if (showAllSchools()) {
        <mat-option [value]="null" class="all-schools-option">
          <mat-icon class="school-icon">select_all</mat-icon>
          <span class="school-name" i18n="@@lib.school-select.allSchools"
            >Alle Schulen</span
          >
        </mat-option>
        } @for (school of schools(); track school.id) {
        <mat-option [value]="school" class="school-option">
          <mat-icon class="school-icon">school</mat-icon>
          <span class="school-name">{{ school.schoolName }}</span>
        </mat-option>
        }
        <!-- <mat-option disabled class="coming-soon-option">
          <span class="coming-soon-text" i18n="@@lib.school-select.moreComing"
            >Weitere Schulen folgen</span
          >
        </mat-option> -->
        @if (schools().length === 0) {
        <mat-option disabled>
          <span class="no-schools-text" i18n="@@lib.school-select.noSchools"
            >Keine Schulen gefunden</span
          >
        </mat-option>
        }
      </mat-select>
    </mat-form-field>
    @if (showDeadline() && selectedDeadline()) {
      <div class="deadline-banner">
        <mat-icon>event</mat-icon>
        <span i18n="@@lib.school-select.registrationDeadline.label">Anmeldefrist:</span>
        <strong>{{ selectedDeadline() }}</strong>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .school-select {
        width: 100%;
      }

      .deadline-banner {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        background: var(--theme-info-surface);
        border-left: 3px solid #1976d2;
        border-radius: 4px;
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.87);
        margin-bottom: 1rem;

        mat-icon {
          color: #1976d2;
          flex-shrink: 0;
        }
      }

      ::ng-deep {
        .mat-mdc-text-field-wrapper {
          background-color: white;
        }

        .school-select-panel {
          margin-top: 4px;
        }

        .school-option {
          display: flex;
          align-items: center;
          height: 48px;
        }

        .all-schools-option {
          display: flex;
          align-items: center;
          height: 48px;
          font-weight: 500;
          border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        }

        .school-icon {
          margin-right: 12px;
          color: rgba(0, 0, 0, 0.54);
        }

        .school-name {
          font-size: 16px;
        }

        .coming-soon-option {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.38);
        }

        .no-schools-text {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.38);
        }
      }
    `,
  ],
})
export class SchoolSelectComponent {
  schools = input<School[]>([]);
  selectedSchool = input<School | null>();
  showAllSchools = input<boolean>(false);
  disabled = input<boolean>(false);
  showDeadline = input<boolean>(true);
  /**
   * Schuljahr, auf das sich die Frist bezieht. Ohne Angabe gilt die nächste
   * anstehende Frist — Tag und Monat sind fix, das Jahr ergibt sich (#746).
   */
  deadlineSchoolYear = input<number | null>(null);
  schoolSelected = output<School>();
  schoolControl = new FormControl<School | null>(null);

  private schoolyearService = inject(SchoolyearService);

  selectedDeadline = computed(() => {
    const deadline = this.selectedSchool()?.registrationDeadline;
    if (!deadline) return '';
    const year = this.deadlineSchoolYear();
    const date =
      year !== null
        ? resolveRegistrationDeadline(deadline, year)
        : getNextRegistrationDeadline(
            deadline,
            this.schoolyearService.getCurrentSchoolYear().year,
          );
    return date ? this.formatDate(date) : '';
  });

  compareSchools(school1: School, school2: School): boolean {
    return school1?.id === school2?.id;
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  constructor() {
    // Watch for form control changes and emit selected school
    this.schoolControl.valueChanges.subscribe((school) => {
      // Emit the school (can be null for "All Schools")
      this.schoolSelected.emit(school || ({} as School));
    });

    // Effect to handle disabled state
    effect(() => {
      const isDisabled = this.disabled();
      if (isDisabled) {
        this.schoolControl.disable({ emitEvent: false });
      } else {
        this.schoolControl.enable({ emitEvent: false });
      }
    });

    // Effect to handle selectedSchool input changes
    effect(() => {
      const selected = this.selectedSchool();
      const currentSchools = this.schools();

      if (selected && currentSchools.length > 0) {
        // Find the matching school from the current list
        const matchingSchool = currentSchools.find((s) => s.id === selected.id);

        if (
          matchingSchool &&
          (!this.schoolControl.value ||
            this.schoolControl.value.id !== matchingSchool.id)
        ) {
          this.schoolControl.setValue(matchingSchool, { emitEvent: false });
        }
      } else if (!selected && this.schoolControl.value) {
        // Clear selection if selectedSchool is null
        this.schoolControl.setValue(null, { emitEvent: false });
      }
    });
  }
}
